import React from 'react';
import {
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import _ from 'lodash';
import { workflowEventOptions, WorkflowEventType } from './constants';

interface Workflow {
  id: string;
  name: string;
  active: boolean;
  description: string;
  json: any;
  createdOn: string;
  modifiedOn: string;
  lastModifiedBy: any;
}

interface WorkflowsTableProps {
  workflows: Workflow[];
  page: number;
  rowsPerPage: number;
  order: 'asc' | 'desc';
  orderBy: keyof Workflow | undefined;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Workflow) => void;
  onEdit: (id: string) => void;
}

const WorkflowsTable: React.FC<WorkflowsTableProps> = ({
  workflows,
  page,
  rowsPerPage,
  order,
  orderBy,
  onPageChange,
  onRowsPerPageChange,
  onRequestSort,
  onEdit,
}) => {
  const getEventLabel = (eventType: WorkflowEventType) => {
    const eventOption = workflowEventOptions.find(option => option.value === eventType);
    return eventOption ? eventOption.label : eventType;
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'name'}
                direction={orderBy === 'name' ? order : 'asc'}
                onClick={event => onRequestSort(event, 'name')}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'description'}
                direction={orderBy === 'description' ? order : 'asc'}
                onClick={event => onRequestSort(event, 'description')}
              >
                Description
              </TableSortLabel>
            </TableCell>
            <TableCell>Status</TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'json'}
                direction={orderBy === 'json' ? order : 'asc'}
                onClick={event => onRequestSort(event, 'json')}
              >
                Event
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'createdOn'}
                direction={orderBy === 'createdOn' ? order : 'asc'}
                onClick={event => onRequestSort(event, 'createdOn')}
              >
                Created
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'modifiedOn'}
                direction={orderBy === 'modifiedOn' ? order : 'asc'}
                onClick={event => onRequestSort(event, 'modifiedOn')}
              >
                Modified
              </TableSortLabel>
            </TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {workflows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => (
            <TableRow key={row.id}>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>
                <Chip
                  label={row.active ? 'ON' : 'OFF'}
                  style={{ backgroundColor: row.active ? '#66BB6A' : '#EF5350', color: 'white' }}
                />
              </TableCell>
              <TableCell>
                {getEventLabel(_.get(row, 'json.event'))}: {_.get(row, 'json.conditions', []).join(' OR ')}
              </TableCell>
              <TableCell>{row.createdOn}</TableCell>
              <TableCell>
                {row.modifiedOn} By <strong>{row.lastModifiedBy.fullName}</strong>
              </TableCell>
              <TableCell>
                <IconButton color="primary" onClick={() => onEdit(row.id)} size="large">
                  <EditIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={workflows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </TableContainer>
  );
};

export default WorkflowsTable;
